import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../../components/common/global-top-bar';
import ContentDetailsModal from '../../components/content-library/content-details-modal';
import Searchbar from '../../components/common/searchbar';
import { Tag, useTags } from '../../hooks/useTags';
import { useBrowseListenerAudio, ListenerAudio, useUpdateListenerAudio } from '../../hooks/useListenerAudio';
import { Resource, useBrowseResources } from '../../hooks/useResources';
import { useSearchForSupportTags } from '../../hooks/useTagGroups';
import {
  CollectionItem,
  ContentCollection,
  ContentCollectionListRequest,
  ContentCollectionCreatePayload,
  useContentCollection,
  useContentCollections,
  useCreateContentCollection,
  useUpdateContentCollection,
  useDeleteContentCollection,
  useDuplicateContentCollection,
  useReorderContentCollections,
} from '../../hooks/useContentCollections';
import { useVerticals } from '../../hooks/useClients';
import AddCollectionItemModal from '../../components/content-library/add-collection-item-modal';
import CreateCollectionModal, {
  CreateCollectionFormValues,
} from '../../components/content-library/create-collection-modal';
import CollectionsFilterModal, {
  CollectionsFilterValues as CollectionsFilterModalValues,
} from '../../components/content-library/collections-filter-modal';
import ResourcesFilterModal from '../../components/content-library/resources-filter-modal';
import StoriesFilterModal from '../../components/content-library/stories-filter-modal';
import { AudioContentTable } from './components/audio-content';
import { VideoContentTable } from './components/video-content';
import { ResourceContentTable } from './components/resource-content';
import { CollectionsContent } from './components/collections-content';
import { getCollectionItemDetailTitle, getCollectionClientIds } from './utils/collection-helpers';
import { useContentLibraryUrlState } from './hooks/useContentLibraryUrlState';
import { buildAudioParams, buildVideoParams, buildResourcesParams } from './utils/browse-params';
import { usePaginationValidation } from './hooks/usePaginationValidation';
import { useTagMap } from './utils/tag-helpers';
import { useCollectionOperations } from './hooks/useCollectionOperations';
import { resetPageForTab } from './utils/page-helpers';

type ContentTab = 'audio' | 'video' | 'resources' | 'collections';

const FiltersButton: React.FC<{ activeCount: number; onClick: () => void }> = ({ activeCount, onClick }) => (
  <button
    type="button"
    className={`h-10 rounded-md border px-4 text-sm font-semibold ${
      activeCount > 0
        ? 'border-blue-primary text-blue-primary bg-blue-50'
        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
    }`}
    onClick={onClick}
  >
    Filters
    {activeCount > 0 && (
      <span className="bg-blue-primary ml-1.5 rounded-full px-1.5 py-0.5 text-xs text-white">{activeCount}</span>
    )}
  </button>
);

const tabs: { key: ContentTab; label: string }[] = [
  { key: 'audio', label: 'Audio' },
  { key: 'video', label: 'Video' },
  { key: 'resources', label: 'Resources' },
  { key: 'collections', label: 'Collections' },
];

const AUDIO_PAGE_LIMIT = 20;
const VIDEO_PAGE_LIMIT = 20;
const RESOURCES_PAGE_LIMIT = 20;
const COLLECTION_PAGE_LIMIT = 20;

interface DetailModalState {
  title: string;
  data: ListenerAudio | Resource;
  tagMap?: Map<number, Tag>;
}

const ContentLibrary: React.FC = () => {
  const {
    activeTab,
    setActiveTab,
    audioPage,
    setAudioPage,
    videoPage,
    setVideoPage,
    resourcesPage,
    setResourcesPage,
    collectionsPage,
    setCollectionsPage,
    searchTerms,
    setSearchTerms,
    sharedFilters,
    setSharedFilters,
    storiesFilters,
    setStoriesFilters,
    resourcesFilters,
    setResourcesFilters,
    collectionsFilters,
    setCollectionsFilters,
  } = useContentLibraryUrlState();

  const [detailModal, setDetailModal] = useState<DetailModalState | null>(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showCreateCollectionModal, setShowCreateCollectionModal] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState<ContentCollection | null>(null);
  const [showStoriesFilterModal, setShowStoriesFilterModal] = useState(false);
  const [showResourcesFilterModal, setShowResourcesFilterModal] = useState(false);
  const [showCollectionsFilterModal, setShowCollectionsFilterModal] = useState(false);

  const { mutateAsync: updateListenerAudio } = useUpdateListenerAudio();
  const { data: verticals, isLoading: isLoadingVerticals } = useVerticals();
  const verticalOptions = useMemo(() => verticals ?? [], [verticals]);
  const activeSearchValue = searchTerms[activeTab];

  const handleSearch = (value: string) => {
    setSearchTerms((prev) => ({ ...prev, [activeTab]: value }));
    resetPageForTab(activeTab, setAudioPage, setVideoPage, setResourcesPage, setCollectionsPage);
    if (activeTab === 'collections') {
      setSelectedCollectionId(null);
      setShowAddItemModal(false);
    }
  };

  const { data: topicTags } = useTags({ include_hidden: true, tag_type: 'topic', limit: 200 });
  const topicTagMap = useTagMap(topicTags);

  const audioParams = useMemo(
    () => buildAudioParams(audioPage, searchTerms.audio, sharedFilters, AUDIO_PAGE_LIMIT, storiesFilters.showTestUsers),
    [audioPage, searchTerms.audio, sharedFilters, storiesFilters.showTestUsers],
  );
  const videoParams = useMemo(
    () => buildVideoParams(videoPage, searchTerms.video, sharedFilters, VIDEO_PAGE_LIMIT, storiesFilters.showTestUsers),
    [videoPage, searchTerms.video, sharedFilters, storiesFilters.showTestUsers],
  );
  const resourcesParams = useMemo(
    () =>
      buildResourcesParams(
        resourcesPage,
        searchTerms.resources,
        sharedFilters,
        RESOURCES_PAGE_LIMIT,
        resourcesFilters.statuses,
      ),
    [resourcesPage, searchTerms.resources, sharedFilters, resourcesFilters.statuses],
  );

  const { data: audioResponse, isLoading: isAudioLoading } = useBrowseListenerAudio(audioParams, {
    enabled: activeTab === 'audio',
  });
  const { data: videoResponse, isLoading: isVideoLoading } = useBrowseListenerAudio(videoParams, {
    enabled: activeTab === 'video',
  });
  const { data: resourcesResponse, isLoading: isResourcesLoading } = useBrowseResources(resourcesParams, {
    enabled: activeTab === 'resources',
  });
  const { data: resourceTags } = useSearchForSupportTags();
  const resourceTagMap = useTagMap(resourceTags);

  const collectionsParams = useMemo<ContentCollectionListRequest>(
    () => ({
      page: sharedFilters.clientId ? 1 : collectionsPage,
      limit: sharedFilters.clientId ? 100 : COLLECTION_PAGE_LIMIT,
      order_by: sharedFilters.clientId ? 'sort_order' : 'created',
      order_direction: sharedFilters.clientId ? 'asc' : 'desc',
      search: searchTerms.collections || undefined,
      tag_groups: sharedFilters.tagGroupIds.length ? sharedFilters.tagGroupIds : undefined,
      vertical_ids: collectionsFilters.verticalIds.length ? collectionsFilters.verticalIds : undefined,
      domain_ids: sharedFilters.domainId ? [sharedFilters.domainId] : undefined,
      client_ids: sharedFilters.clientId ? [sharedFilters.clientId] : undefined,
    }),
    [
      collectionsPage,
      searchTerms.collections,
      sharedFilters.tagGroupIds,
      sharedFilters.domainId,
      sharedFilters.clientId,
      collectionsFilters.verticalIds,
    ],
  );

  const { data: collectionsResponse, isLoading: isCollectionsLoading } = useContentCollections(collectionsParams, {
    enabled: activeTab === 'collections',
  });
  const { data: selectedCollection, isLoading: isCollectionLoading } = useContentCollection(
    selectedCollectionId ?? undefined,
  );
  const { mutateAsync: createCollection, isLoading: isCreatingCollection } = useCreateContentCollection();
  const { mutateAsync: updateCollection, isLoading: isUpdatingCollection } = useUpdateContentCollection();
  const { mutateAsync: deleteCollection, isLoading: isDeletingCollection } = useDeleteContentCollection();
  const { mutateAsync: duplicateCollection, isLoading: isDuplicatingCollection } = useDuplicateContentCollection();
  const { mutateAsync: reorderCollections, isLoading: isReorderingCollections } = useReorderContentCollections();

  const handleReorderCollections = useCallback(
    async (reorderedCollections: ContentCollection[]) => {
      await reorderCollections({
        collectionIds: reorderedCollections.map((c) => c.id),
        clientId: sharedFilters.clientId!,
        listParams: collectionsParams,
      });
    },
    [reorderCollections, collectionsParams, sharedFilters.clientId],
  );

  const resources = resourcesResponse?.data ?? [];
  const resourceCount = resourcesResponse?.count ?? 0;
  const collections = collectionsResponse?.data ?? [];
  const collectionsCount = collectionsResponse?.count ?? 0;

  // Only allow DnD when the full unfiltered client list is showing, so PUT
  // /sort_order always receives every sibling collection for that client.
  const canReorderCollections =
    !!sharedFilters.clientId &&
    !searchTerms.collections &&
    sharedFilters.tagGroupIds.length === 0 &&
    !sharedFilters.domainId &&
    collectionsFilters.verticalIds.length === 0 &&
    collectionsCount <= (collectionsParams.limit ?? 100);

  usePaginationValidation(audioResponse?.count, audioPage, AUDIO_PAGE_LIMIT, setAudioPage);
  usePaginationValidation(videoResponse?.count, videoPage, VIDEO_PAGE_LIMIT, setVideoPage);
  usePaginationValidation(resourceCount, resourcesPage, RESOURCES_PAGE_LIMIT, setResourcesPage);
  usePaginationValidation(collectionsCount, collectionsPage, COLLECTION_PAGE_LIMIT, setCollectionsPage);

  const existingCollectionListenerAudioIds = useMemo<number[]>(() => {
    if (!selectedCollection) return [];
    return selectedCollection.items
      .filter((item) => item.listener_audio_id !== null && item.listener_audio_id !== undefined)
      .map((item) => item.listener_audio_id!) as number[];
  }, [selectedCollection]);

  const existingCollectionResourceIds = useMemo<number[]>(() => {
    if (!selectedCollection) return [];
    return selectedCollection.items
      .filter((item) => item.resource_id !== null && item.resource_id !== undefined)
      .map((item) => item.resource_id!) as number[];
  }, [selectedCollection]);

  const handleSelectCollection = useCallback((collectionId: number) => {
    setSelectedCollectionId(collectionId);
    setShowAddItemModal(false);
  }, []);

  const handleBackToCollections = useCallback(() => {
    setSelectedCollectionId(null);
    setShowAddItemModal(false);
  }, []);

  const {
    handleItemAdd: handleCollectionItemAdd,
    handleItemRemove: handleCollectionItemRemove,
    handleItemReorder: handleCollectionItemReorder,
    handleUpdateVerticals: handleUpdateCollectionVerticals,
    handleUpdateTags: handleUpdateCollectionTags,
    handleUpdateDomain: handleUpdateCollectionDomain,
    handleUpdateClient: handleUpdateCollectionClient,
  } = useCollectionOperations({
    selectedCollection: selectedCollection || undefined,
    updateCollection,
  });

  const handleShowCollectionItemDetails = useCallback(
    (item: CollectionItem) => {
      if (!item || !item.data) return;
      const title = getCollectionItemDetailTitle(item);
      const tagMap =
        item.contentType === 'resource'
          ? resourceTagMap
          : item.contentType === 'audio' || item.contentType === 'video'
            ? topicTagMap
            : undefined;

      if (item.contentType === 'resource' || item.contentType === 'audio' || item.contentType === 'video') {
        setDetailModal({
          title,
          data: item.data as ListenerAudio | Resource,
          tagMap,
        });
      }
    },
    [resourceTagMap, topicTagMap],
  );

  const handleCreateCollectionSubmit = useCallback(
    async (values: CreateCollectionFormValues) => {
      const payload: ContentCollectionCreatePayload = {
        name: values.name,
        description: values.description ? values.description : null,
        visibility: values.visibility,
        client_ids: values.clientIds.length ? values.clientIds : [],
        domain_id: values.domainId ?? null,
        vertical_ids: values.verticalIds,
        tag_ids: values.tagIds.length > 0 ? values.tagIds : null,
      };

      try {
        const newCollection = await createCollection(payload);
        setShowCreateCollectionModal(false);
        if (newCollection?.id) {
          setSelectedCollectionId(newCollection.id);
          setShowAddItemModal(false);
        }
      } catch (error) {
        // handled by mutation toast
      }
    },
    [createCollection],
  );

  const handleToggleCollectionVisibility = useCallback(
    async (nextVisibility: boolean) => {
      if (!selectedCollection) return;
      try {
        await updateCollection({
          collectionId: selectedCollection.id,
          payload: { visibility: nextVisibility },
        });
      } catch (error) {
        // handled by mutation toast
      }
    },
    [selectedCollection, updateCollection],
  );

  const handleToggleCollectionPublicEmbed = useCallback(
    async (nextIsPublic: boolean) => {
      if (!selectedCollection) return;
      try {
        await updateCollection({
          collectionId: selectedCollection.id,
          payload: { is_public: nextIsPublic },
        });
      } catch (error) {
        // handled by mutation toast
      }
    },
    [selectedCollection, updateCollection],
  );

  const handleUpdateCollectionDescription = useCallback(
    async (description: string | null) => {
      if (!selectedCollection) return;
      try {
        await updateCollection({
          collectionId: selectedCollection.id,
          payload: { description },
        });
      } catch (error) {
        // handled by mutation toast
      }
    },
    [selectedCollection, updateCollection],
  );

  const handleUpdateCollectionName = useCallback(
    async (name: string) => {
      if (!selectedCollection) return;
      try {
        await updateCollection({
          collectionId: selectedCollection.id,
          payload: { name },
        });
      } catch (error) {
        // handled by mutation toast
      }
    },
    [selectedCollection, updateCollection],
  );

  const handleDeleteCollection = useCallback(async () => {
    if (!selectedCollection) return;
    try {
      await deleteCollection(selectedCollection.id);
      setSelectedCollectionId(null);
      setShowAddItemModal(false);
    } catch (error) {
      // handled by mutation toast
    }
  }, [selectedCollection, deleteCollection]);

  const handleOpenDuplicateModal = useCallback((collection: ContentCollection) => {
    setDuplicateSource(collection);
  }, []);

  const handleDuplicateSubmit = useCallback(
    async (values: CreateCollectionFormValues) => {
      if (!duplicateSource) return;
      const payload: ContentCollectionCreatePayload = {
        name: values.name,
        description: values.description ? values.description : null,
        visibility: values.visibility,
        client_ids: values.clientIds.length ? values.clientIds : [],
        domain_id: values.domainId ?? null,
        vertical_ids: values.verticalIds,
        tag_ids: values.tagIds.length > 0 ? values.tagIds : null,
      };

      try {
        const created = await duplicateCollection({ source: duplicateSource, payload });
        setDuplicateSource(null);
        if (created?.id) {
          setSelectedCollectionId(created.id);
          setShowAddItemModal(false);
        }
      } catch (error) {
        // handled by mutation toast
      }
    },
    [duplicateSource, duplicateCollection],
  );

  const duplicateInitialValues = useMemo<Partial<CreateCollectionFormValues> | undefined>(() => {
    if (!duplicateSource) return undefined;
    return {
      name: `Copy of ${duplicateSource.name}`,
      description: duplicateSource.description ?? '',
      visibility: duplicateSource.visibility,
      clientIds: getCollectionClientIds(duplicateSource),
      selectedClients: (duplicateSource.clients || []).map((c) => ({
        id: c.id,
        name: c.name || `Client #${c.id}`,
      })),
      domainId: duplicateSource.domain_id ?? null,
      verticalIds: duplicateSource.verticals.map((vertical) => vertical.id),
      tagIds: duplicateSource.tags.map((tag) => tag.id),
    };
  }, [duplicateSource]);

  const renderContent = () => {
    const contentMap: Record<ContentTab, React.ReactNode> = {
      audio: (
        <AudioContentTable
          rows={audioResponse?.data || []}
          tagMap={topicTagMap}
          tags={topicTags || []}
          isLoading={isAudioLoading}
          count={audioResponse?.count ?? 0}
          currentPage={audioPage}
          limit={AUDIO_PAGE_LIMIT}
          onPageChange={setAudioPage}
          onShowDetails={(experience) =>
            setDetailModal({ title: `Audio ${experience.id}`, data: experience, tagMap: topicTagMap })
          }
        />
      ),
      video: (
        <VideoContentTable
          rows={videoResponse?.data || []}
          tagMap={topicTagMap}
          tags={topicTags || []}
          isLoading={isVideoLoading}
          count={videoResponse?.count ?? 0}
          currentPage={videoPage}
          limit={VIDEO_PAGE_LIMIT}
          onPageChange={setVideoPage}
          onShowDetails={(experience) =>
            setDetailModal({ title: `Video ${experience.id}`, data: experience, tagMap: topicTagMap })
          }
        />
      ),
      resources: (
        <ResourceContentTable
          rows={resources}
          tagMap={resourceTagMap}
          tags={resourceTags || []}
          isLoading={isResourcesLoading}
          count={resourceCount}
          currentPage={resourcesPage}
          limit={RESOURCES_PAGE_LIMIT}
          onPageChange={setResourcesPage}
          onShowDetails={(resource) =>
            setDetailModal({ title: `Resource ${resource.id}`, data: resource, tagMap: resourceTagMap })
          }
        />
      ),
      collections: (
        <CollectionsContent
          collections={collections}
          isLoadingList={isCollectionsLoading}
          count={collectionsCount}
          currentPage={collectionsPage}
          limit={collectionsParams.limit ?? COLLECTION_PAGE_LIMIT}
          onPageChange={setCollectionsPage}
          onSelectCollection={handleSelectCollection}
          selectedCollectionId={selectedCollectionId}
          selectedCollection={selectedCollection}
          isLoadingCollection={isCollectionLoading}
          onBack={handleBackToCollections}
          onOpenAddItem={() => setShowAddItemModal(true)}
          onRemoveItem={handleCollectionItemRemove}
          onShowItemDetails={handleShowCollectionItemDetails}
          isUpdatingCollection={isUpdatingCollection || isDeletingCollection || isReorderingCollections}
          onToggleVisibility={handleToggleCollectionVisibility}
          onTogglePublicEmbed={handleToggleCollectionPublicEmbed}
          availableVerticals={verticalOptions}
          isLoadingVerticals={isLoadingVerticals}
          onUpdateVerticals={handleUpdateCollectionVerticals}
          onUpdateTags={handleUpdateCollectionTags}
          onUpdateDomain={handleUpdateCollectionDomain}
          onUpdateClient={handleUpdateCollectionClient}
          onReorderItems={handleCollectionItemReorder}
          onUpdateDescription={handleUpdateCollectionDescription}
          onUpdateName={handleUpdateCollectionName}
          onDeleteCollection={handleDeleteCollection}
          onDuplicateCollection={handleOpenDuplicateModal}
          canReorderCollections={canReorderCollections}
          onReorderCollections={handleReorderCollections}
        />
      ),
    };

    return contentMap[activeTab] || null;
  };

  return (
    <div>
      <Header heading="Content Library" />
      <div className="flex flex-col gap-3 px-6 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full max-w-sm">
          <Searchbar key={activeTab} initialValue={activeSearchValue} search={handleSearch} />

          {(activeTab === 'audio' || activeTab === 'video') && (
            <FiltersButton
              activeCount={
                sharedFilters.tagGroupIds.length +
                sharedFilters.tagIds.length +
                (sharedFilters.domainId ? 1 : 0) +
                (storiesFilters.showTestUsers ? 1 : 0)
              }
              onClick={() => setShowStoriesFilterModal(true)}
            />
          )}
          {activeTab === 'resources' && (
            <FiltersButton
              activeCount={
                sharedFilters.tagGroupIds.length +
                sharedFilters.tagIds.length +
                (sharedFilters.domainId ? 1 : 0) +
                (sharedFilters.clientId ? 1 : 0) +
                resourcesFilters.statuses.length
              }
              onClick={() => setShowResourcesFilterModal(true)}
            />
          )}
          {activeTab === 'collections' && (
            <FiltersButton
              activeCount={
                sharedFilters.tagGroupIds.length +
                (sharedFilters.domainId ? 1 : 0) +
                (sharedFilters.clientId ? 1 : 0) +
                collectionsFilters.verticalIds.length
              }
              onClick={() => setShowCollectionsFilterModal(true)}
            />
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {activeTab === 'collections' && (
            <button
              className="bg-blue-primary h-10 rounded-md px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => setShowCreateCollectionModal(true)}
              disabled={isCreatingCollection}
            >
              Create Collection
            </button>
          )}
        </div>
      </div>
      <div className="flex border-b border-gray-200">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              className={`mr-4 border-b-2 px-4 py-3 text-sm font-semibold ${
                isActive ? 'border-blue-primary text-blue-primary' : 'text-gray-dark border-transparent'
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div className="px-6 py-6">{renderContent()}</div>
      <CreateCollectionModal
        isOpen={showCreateCollectionModal}
        onClose={() => setShowCreateCollectionModal(false)}
        onSubmit={handleCreateCollectionSubmit}
        isSubmitting={isCreatingCollection}
        verticals={verticalOptions}
        isLoadingVerticals={isLoadingVerticals}
      />
      <CreateCollectionModal
        isOpen={duplicateSource !== null}
        onClose={() => {
          if (!isDuplicatingCollection) {
            setDuplicateSource(null);
          }
        }}
        onSubmit={handleDuplicateSubmit}
        isSubmitting={isDuplicatingCollection}
        verticals={verticalOptions}
        isLoadingVerticals={isLoadingVerticals}
        mode="duplicate"
        initialValues={duplicateInitialValues}
      />
      <StoriesFilterModal
        isOpen={showStoriesFilterModal}
        onClose={() => setShowStoriesFilterModal(false)}
        onApply={(values) => {
          setSharedFilters({
            tagGroupIds: values.tagGroupIds,
            tagIds: values.tagIds,
            domainId: values.domainId,
            clientId: sharedFilters.clientId,
          });
          setStoriesFilters({ showTestUsers: values.showTestUsers });
          resetPageForTab(activeTab, setAudioPage, setVideoPage, setResourcesPage, setCollectionsPage);
        }}
        initialValues={{
          ...sharedFilters,
          showTestUsers: storiesFilters.showTestUsers,
        }}
      />
      <ResourcesFilterModal
        isOpen={showResourcesFilterModal}
        onClose={() => setShowResourcesFilterModal(false)}
        onApply={(values) => {
          setSharedFilters({
            tagGroupIds: values.tagGroupIds,
            tagIds: values.tagIds,
            domainId: values.domainId,
            clientId: values.clientId,
          });
          setResourcesFilters({ statuses: values.statuses });
          setResourcesPage(1);
        }}
        initialValues={{
          ...sharedFilters,
          statuses: resourcesFilters.statuses,
        }}
      />
      <CollectionsFilterModal
        isOpen={showCollectionsFilterModal}
        onClose={() => setShowCollectionsFilterModal(false)}
        onApply={(values: CollectionsFilterModalValues) => {
          setSharedFilters({
            tagGroupIds: values.tagGroupIds,
            tagIds: sharedFilters.tagIds,
            domainId: values.domainId,
            clientId: values.clientId,
          });
          setCollectionsFilters({ verticalIds: values.verticalIds });
          setCollectionsPage(1);
        }}
        initialValues={{
          tagGroupIds: sharedFilters.tagGroupIds,
          verticalIds: collectionsFilters.verticalIds,
          domainId: sharedFilters.domainId,
          clientId: sharedFilters.clientId,
        }}
      />
      {showAddItemModal && selectedCollection && (
        <AddCollectionItemModal
          isOpen={showAddItemModal}
          onClose={() => setShowAddItemModal(false)}
          existingListenerAudioIds={existingCollectionListenerAudioIds}
          existingResourceIds={existingCollectionResourceIds}
          onSelect={handleCollectionItemAdd}
          isBusy={isUpdatingCollection}
          audioTagMap={topicTagMap}
          resourceTagMap={resourceTagMap}
        />
      )}
      {detailModal && (
        <ContentDetailsModal
          title={detailModal.title}
          data={detailModal.data}
          tagMap={detailModal.tagMap}
          onClose={() => setDetailModal(null)}
          onSaveListenerAudio={
            'listener_role_id' in detailModal.data
              ? (payload) => {
                  const audio = detailModal.data as ListenerAudio;
                  updateListenerAudio({
                    listenerId: audio.listener_role_id,
                    audioId: audio.id,
                    payload: {
                      topic_tag_ids: audio.topic_tag_ids ?? [],
                      ...payload,
                    },
                  }).then(() => setDetailModal(null));
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default ContentLibrary;
